// The dashboard: a plain page head, then the widgets this person arranged, or their
// role's default until they arrange their own. Arranging is always on, as in the AWS
// console: drag a widget by its header, resize it from its corner, remove it from its
// menu, drag new ones in from the widget panel. Each change is saved as it is dropped.
// The arrangement lives in the database (/Dashboard/layout); every widget reads
// through routes that already check permissions and follows its live topics.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Check, Info, Loader2, Plus, RotateCcw, Settings, Trash2, X } from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import { loadDashboardLayout, resetDashboardLayout, saveDashboardLayout } from './api';
import { tx } from './i18n';
import { Failed } from './parts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { roleName, t } from './DashboardData';
import { SkeletonKpi, SkeletonChart } from './DashboardParts';
import DashboardGrid, { WidgetPanel, bottomOf } from './DashboardGrid';
import './dashboard.css';

function Placeholder() {
  return (
    <div className="xo-dash-placeholder" aria-busy="true" aria-label={t('page.loading')}>
      {[1, 2, 3, 4].map((index) => <section key={`k${index}`} className="xo-dash-card is-kpi"><SkeletonKpi /></section>)}
      {[1, 2].map((index) => <section key={`c${index}`} className="xo-dash-card is-wide"><SkeletonChart /></section>)}
    </div>
  );
}

function useToday() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);
  return new Date(now);
}

// Saving follows the hand: the latest arrangement is sent once it is let go; one
// changed while a save is on its way is sent right after it.
function useAutosave(onSaved) {
  const [state, setState] = useState({ status: 'idle', error: null });
  const queue = useRef({ next: null, busy: false, failed: null });
  const answered = useRef(onSaved);
  answered.current = onSaved;
  const timer = useRef(null);
  const flush = useRef(null);
  flush.current = async () => {
    const waiting = queue.current;
    if (waiting.busy || waiting.next === null) return;
    const widgets = waiting.next;
    waiting.next = null;
    waiting.busy = true;
    setState({ status: 'saving', error: null });
    try {
      const answer = await saveDashboardLayout(widgets);
      waiting.failed = null;
      answered.current(answer, waiting.next !== null);
      setState({ status: 'saved', error: null });
    } catch (error) {
      waiting.failed = widgets;
      setState({ status: 'failed', error: errorMessage(error) });
    } finally {
      waiting.busy = false;
      if (waiting.next !== null) flush.current();
    }
  };
  const save = useCallback((widgets) => { queue.current.next = widgets; flush.current(); }, []);
  const retry = useCallback(() => { if (queue.current.failed) save(queue.current.failed); }, [save]);
  // "Saved" shows for a moment, then the corner is quiet again.
  useEffect(() => {
    if (state.status !== 'saved') return undefined;
    timer.current = window.setTimeout(() => setState({ status: 'idle', error: null }), 1800);
    return () => window.clearTimeout(timer.current);
  }, [state.status]);
  return { state, save, retry };
}

// The dashboard's settings, beside Add widget: back to the role's default, or clear
// every widget away. Each asks once, in the menu, before it acts.
function SettingsMenu({ role, busy, canClear, onReset, onClear }) {
  const [open, setOpen] = useState(false);
  const [asking, setAsking] = useState(null);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !(box.current && box.current.contains(event.target))) {
        setOpen(false);
        setAsking(null);
      }
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', close);
    return () => { window.removeEventListener('pointerdown', close); window.removeEventListener('keydown', close); };
  }, [open]);
  const act = () => {
    const chosen = asking;
    setOpen(false);
    setAsking(null);
    if (chosen === 'reset') onReset(); else onClear();
  };
  return (
    <div className="xo-dash-settings" ref={box}>
      <button type="button" className="xo-dash-ghost-button is-icon" disabled={busy} aria-haspopup="menu" aria-expanded={open}
        aria-label={t('edit.settings')} title={t('edit.settings')} onClick={() => { setOpen((shown) => !shown); setAsking(null); }}>
        <Settings aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div className="xo-dash-settings-menu" role="menu" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}>
            {asking === null ? (
              <>
                <button type="button" role="menuitem" onClick={() => setAsking('reset')}><RotateCcw aria-hidden="true" />{t('edit.reset')}</button>
                <button type="button" role="menuitem" className="is-danger" disabled={!canClear} onClick={() => setAsking('clear')}>
                  <Trash2 aria-hidden="true" />{t('edit.clear')}
                </button>
              </>
            ) : (
              <div className="xo-dash-settings-confirm">
                <p>{asking === 'reset' ? t('edit.reset_confirm', { role }) : t('edit.clear_confirm')}</p>
                <div>
                  <button type="button" className="xo-dash-ghost-button" onClick={() => setAsking(null)}>{t(asking === 'reset' ? 'edit.keep_mine' : 'edit.keep_them')}</button>
                  <button type="button" className="xo-dash-primary is-danger" onClick={act}>{t(asking === 'reset' ? 'edit.reset_now' : 'edit.clear_now')}</button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Saving shows as a spinner, then a tick for a moment; a failed save as a warning
// that tries again when pressed.
function SaveState({ state, onRetry }) {
  if (state.status === 'failed') {
    const words = `${t('edit.save_failed')} ${t('card.retry')}`;
    return (
      <button type="button" className="xo-dash-save is-failed" role="alert" onClick={onRetry} title={state.error ? `${words} (${state.error})` : words} aria-label={words}>
        <AlertTriangle aria-hidden="true" />
      </button>
    );
  }
  if (state.status === 'idle') return null;
  const words = t(state.status === 'saving' ? 'edit.saving' : 'edit.saved');
  return (
    <span className={`xo-dash-save is-${state.status}`} role="status" aria-label={words} title={words}>
      {state.status === 'saving' ? <Loader2 className="xo-spin" aria-hidden="true" /> : <Check aria-hidden="true" />}
    </span>
  );
}

// Drawn afresh whenever the role or its permissions change, as a role preview does.
export default function XomicsOverview() {
  const { user, permissions } = useXomicsAuth();
  const permissionKey = [...permissions].sort().join(',');
  return <Dashboard key={`${user.roleCode}|${permissionKey}`} user={user} permissionKey={permissionKey} />;
}

function Dashboard({ user, permissionKey }) {
  const resource = useLiveResource([], loadDashboardLayout, { reconcileMs: 0 });
  const today = useToday();
  const [layout, setLayout] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [dropping, setDropping] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState(null);
  const [droppedSeen, setDroppedSeen] = useState(false);

  useEffect(() => {
    if (resource.data) { setLayout(resource.data); setWidgets(resource.data.widgets); setDroppedSeen(false); }
  }, [resource.data]);

  // The server's answer to a save is what was sent, fitted; it replaces what is drawn
  // only when nothing newer is waiting to be sent.
  const saved = useCallback((answer, newer) => {
    setLayout(answer);
    if (!newer) setWidgets(answer.widgets);
  }, []);
  const { state: saveState, save, retry } = useAutosave(saved);
  const change = useCallback((next) => { setWidgets(next); save(next); }, [save]);

  const editable = layout !== null && !layout.previewing;
  const allowed = useMemo(() => permissionKey.split(',').filter(Boolean), [permissionKey]);
  const placed = useMemo(() => new Set((widgets || []).map((entry) => entry.code)), [widgets]);
  const addable = layout ? layout.library.filter((spec) => !placed.has(spec.code)).length : 0;

  const add = useCallback((spec) => {
    change([...widgets, { code: spec.code, x: 0, y: bottomOf(widgets), w: spec.defaultWidth, h: spec.defaultHeight }]);
  }, [change, widgets]);
  const reset = useCallback(async () => {
    setResetting(true);
    setResetError(null);
    try {
      const answer = await resetDashboardLayout();
      setLayout(answer);
      setWidgets(answer.widgets);
    } catch (error) {
      setResetError(errorMessage(error));
    } finally {
      setResetting(false);
    }
  }, []);

  const place = user.location ? [user.location.name, user.location.city].filter(Boolean).join(', ') : null;
  const previewing = Boolean(user.actualRoleCode && user.actualRoleCode !== user.roleCode);
  return (
    <div className="xo-dash">
      <header className="xo-dash-head">
        <div>
          <h1>{tx('ui.nav.dashboard_d87f47b4')}</h1>
          <p>
            <span>{today.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}</span>
            <span>{roleName(user.roleCode)}</span>
            {place && <span>{place}</span>}
            {previewing && <span className="is-preview">{t('hero.previewing')}</span>}
          </p>
        </div>
        <div className="xo-dash-head-actions">
          <SaveState state={saveState} onRetry={retry} />
          {editable && (
            <SettingsMenu role={roleName(layout.role)} busy={resetting} canClear={placed.size > 0} onReset={reset} onClear={() => change([])} />
          )}
          {editable && (
            <button type="button" className="xo-dash-primary" onClick={() => setPanelOpen(true)} disabled={addable === 0 && !panelOpen}>
              <Plus aria-hidden="true" />{t('edit.add_widget')}
            </button>
          )}
        </div>
      </header>
      {resetError && <p className="xo-dash-notice is-bad" role="alert"><Info aria-hidden="true" />{resetError}</p>}
      {layout !== null && layout.previewing && (
        <p className="xo-dash-notice"><Info aria-hidden="true" />{t('page.previewing', { role: roleName(layout.role) })}</p>
      )}
      <AnimatePresence>
        {layout !== null && layout.dropped.length > 0 && !droppedSeen && (
          <motion.p className="xo-dash-notice" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <Info aria-hidden="true" />
            {t(layout.dropped.length === 1 ? 'page.dropped_one' : 'page.dropped', { count: layout.dropped.length })}
            <button type="button" className="xo-dash-icon-button" onClick={() => setDroppedSeen(true)} aria-label={t('page.dismiss')}><X aria-hidden="true" /></button>
          </motion.p>
        )}
      </AnimatePresence>
      {layout === null && resource.error && <Failed message={t('page.layout_failed', { reason: resource.error })} onRetry={resource.refresh} />}
      {layout === null && !resource.error && <Placeholder />}
      {layout !== null && widgets !== null && (
        <DashboardGrid widgets={widgets} library={layout.library} columns={layout.columns} editable={editable} permissions={allowed}
          dropping={dropping} onChange={change} onDropped={() => setDropping(null)} onAdd={() => setPanelOpen(true)} />
      )}
      <AnimatePresence>
        {panelOpen && layout !== null && (
          <WidgetPanel library={layout.library} placed={placed} canDrag={window.matchMedia('(min-width: 900px)').matches}
            onDragStart={setDropping} onDragEnd={() => setDropping(null)} onAdd={add} onClose={() => { setPanelOpen(false); setDropping(null); }} />
        )}
      </AnimatePresence>
    </div>
  );
}
